import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { DatePicker } from '@material-ui/pickers';
import { DateTime } from 'luxon';

interface FieldFilterConfig {
  fieldId: string;
  fieldName: string;
  fieldType: string;
  taskId: string;
  taskTitle: string;
  valueFilter?: ValueFilterConfig;
}

interface TaskFilterConfig {
  taskId: string;
  taskTitle: string;
  completionFilter?: CompletionFilterConfig;
  fields: FieldFilterConfig[];
}

interface CompletionFilterConfig {
  type: 'completed' | 'not_completed' | 'completed_on' | 'completed_before' | 'completed_after' | 'completed_between';
  date?: string;
  dateFrom?: string;
  dateTo?: string;
}

interface ValueFilterConfig {
  type:
    | 'contains'
    | 'equals'
    | 'starts_with'
    | 'greater_than'
    | 'less_than'
    | 'equals_number'
    | 'on_date'
    | 'before_date'
    | 'after_date'
    | 'between_dates'
    | 'has_attachment'
    | 'option_selected';
  value?: string | number;
  dateValue?: string;
  dateFrom?: string;
  dateTo?: string;
  selectedOptions?: string[];
  optionLogic?: 'AND' | 'OR';
}

const useStyles = makeStyles(() =>
  createStyles({
    filterConfigBox: {
      backgroundColor: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
    },
    fieldBox: {
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '12px',
      marginTop: '12px',
    },
    fieldTitle: {
      fontSize: '15px',
      fontWeight: 700,
      color: '#111827',
      marginBottom: '4px',
    },
    fieldSubtitle: {
      fontSize: '10px',
      fontWeight: 500,
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '4px',
    },
    checkboxLabel: {
      fontSize: '13px',
      fontWeight: 400,
      color: '#4b5563',
    },
    formControl: {
      '& .MuiInputLabel-root': {
        fontSize: '11px',
        color: '#6b7280',
      },
      '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
        fontSize: '12px',
      },
      '& .MuiOutlinedInput-root': {
        fontSize: '13px',
        '& fieldset': {
          borderColor: '#e5e7eb',
        },
        '&:hover fieldset': {
          borderColor: '#7047EB',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#7047EB',
        },
      },
      '& .MuiSelect-select': {
        fontSize: '13px !important',
        fontWeight: '400 !important',
        padding: '8px 14px !important',
        lineHeight: '1.3 !important',
      },
      '& .MuiInputBase-input': {
        fontSize: '13px !important',
        fontWeight: '400 !important',
        padding: '6px 14px !important',
        lineHeight: '1.3 !important',
      },
    },
    menuItem: {
      fontSize: '13px',
      padding: '8px 16px',
      '&.Mui-selected': {
        backgroundColor: '#f5f3ff',
      },
      '&.Mui-selected:hover': {
        backgroundColor: '#ede9fe',
      },
      '&:hover': {
        backgroundColor: '#f9fafb',
      },
    },
  })
);

interface TaskFilterConfiguratorProps {
  taskConfig: TaskFilterConfig;
  tasks: any[];
  onUpdateTask: (updates: { completionFilter?: CompletionFilterConfig }) => void;
  onUpdateField: (fieldId: string, updates: Partial<FieldFilterConfig>) => void;
}

export const TaskFilterConfigurator = ({
  taskConfig,
  tasks,
  onUpdateTask,
  onUpdateField,
}: TaskFilterConfiguratorProps) => {
  const classes = useStyles();
  const [showCompletion, setShowCompletion] = useState(!!taskConfig.completionFilter);
  const [fieldStates, setFieldStates] = useState<Record<string, { showValue: boolean }>>({});

  const task = tasks.find((t: any) => t.taskId === taskConfig.taskId);

  const toggleFieldValue = (fieldId: string) => {
    setFieldStates((prev) => ({
      ...prev,
      [fieldId]: { showValue: !prev[fieldId]?.showValue },
    }));
  };

  const renderFieldValueFilter = (fieldConfig: FieldFilterConfig) => {
    const { fieldType } = fieldConfig;
    const fieldItem = task?.taskItems?.find((item: any) => item?.field?.taskFieldId === fieldConfig.fieldId);
    const field = fieldItem?.field;
    const fieldOptions =
      field?.dropdownOptions ||
      field?.multipleChoiceOptions ||
      field?.checkboxOptions ||
      field?.quizTaskFieldOptions ||
      [];

    // Text fields
    if (fieldType.includes('Text') || fieldType.includes('Email') || fieldType.includes('Phone')) {
      return (
        <Box>
          <FormControl
            variant='outlined'
            fullWidth
            size='small'
            className={classes.formControl}
            style={{ marginBottom: '8px' }}
          >
            <InputLabel>Match Type</InputLabel>
            <Select
              value={fieldConfig.valueFilter?.type || 'contains'}
              onChange={(e) =>
                onUpdateField(fieldConfig.fieldId, {
                  valueFilter: { ...fieldConfig.valueFilter, type: e.target.value as any },
                })
              }
              label='Match Type'
              style={{ fontSize: '13px' }}
              inputProps={{ style: { fontSize: '13px', padding: '6px 14px' } }}
            >
              <MenuItem value='contains' className={classes.menuItem}>
                Contains
              </MenuItem>
              <MenuItem value='equals' className={classes.menuItem}>
                Equals
              </MenuItem>
              <MenuItem value='starts_with' className={classes.menuItem}>
                Starts with
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            variant='outlined'
            size='small'
            label='Value'
            className={classes.formControl}
            value={fieldConfig.valueFilter?.value || ''}
            onChange={(e) =>
              onUpdateField(fieldConfig.fieldId, {
                valueFilter: { ...fieldConfig.valueFilter, value: e.target.value },
              })
            }
          />
        </Box>
      );
    }

    // Number fields
    if (fieldType.includes('Number')) {
      return (
        <Box>
          <FormControl
            variant='outlined'
            fullWidth
            size='small'
            className={classes.formControl}
            style={{ marginBottom: '8px' }}
          >
            <InputLabel>Comparison</InputLabel>
            <Select
              value={fieldConfig.valueFilter?.type || 'equals_number'}
              onChange={(e) =>
                onUpdateField(fieldConfig.fieldId, {
                  valueFilter: { ...fieldConfig.valueFilter, type: e.target.value as any },
                })
              }
              label='Comparison'
              style={{ fontSize: '13px' }}
              inputProps={{ style: { fontSize: '13px', padding: '6px 14px' } }}
            >
              <MenuItem value='equals_number' className={classes.menuItem}>
                Equals
              </MenuItem>
              <MenuItem value='greater_than' className={classes.menuItem}>
                Greater than
              </MenuItem>
              <MenuItem value='less_than' className={classes.menuItem}>
                Less than
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            variant='outlined'
            size='small'
            type='number'
            label='Value'
            className={classes.formControl}
            value={fieldConfig.valueFilter?.value || ''}
            onChange={(e) =>
              onUpdateField(fieldConfig.fieldId, {
                valueFilter: { ...fieldConfig.valueFilter, value: parseFloat(e.target.value) },
              })
            }
          />
        </Box>
      );
    }

    // Attachment fields
    if (fieldType.includes('Attachment')) {
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={fieldConfig.valueFilter?.type === 'has_attachment'}
              onChange={(e) =>
                onUpdateField(fieldConfig.fieldId, {
                  valueFilter: { type: e.target.checked ? 'has_attachment' : undefined },
                })
              }
              size='small'
              style={{
                padding: 0,
                marginRight: '6px',
                color: fieldConfig.valueFilter?.type === 'has_attachment' ? '#7047EB' : undefined,
              }}
            />
          }
          label={<Typography className={classes.checkboxLabel}>Has attachment</Typography>}
          style={{ marginLeft: 0 }}
        />
      );
    }

    // Option-based fields
    if (fieldOptions.length > 0) {
      return (
        <Box>
          <FormControl
            variant='outlined'
            fullWidth
            size='small'
            className={classes.formControl}
            style={{ marginBottom: '8px' }}
          >
            <InputLabel>Match Logic</InputLabel>
            <Select
              value={fieldConfig.valueFilter?.optionLogic || 'OR'}
              onChange={(e) =>
                onUpdateField(fieldConfig.fieldId, {
                  valueFilter: { ...fieldConfig.valueFilter, optionLogic: e.target.value as 'AND' | 'OR' },
                })
              }
              label='Match Logic'
              style={{ fontSize: '13px' }}
              inputProps={{ style: { fontSize: '13px', padding: '6px 14px' } }}
            >
              <MenuItem value='OR' className={classes.menuItem}>
                Match ANY selected option (OR)
              </MenuItem>
              <MenuItem value='AND' className={classes.menuItem}>
                Match ALL selected options (AND)
              </MenuItem>
            </Select>
          </FormControl>
          {fieldOptions.map((option: any) => {
            const optionId =
              option.dropdownFieldOptionId ||
              option.multipleChoiceFieldOptionId ||
              option.checkboxFieldOptionId ||
              option.quizTaskFieldOptionId;
            const selectedOptions = fieldConfig.valueFilter?.selectedOptions || [];

            return (
              <FormControlLabel
                key={optionId}
                control={
                  <Checkbox
                    checked={selectedOptions.includes(optionId)}
                    onChange={(e) => {
                      const newOptions = e.target.checked
                        ? [...selectedOptions, optionId]
                        : selectedOptions.filter((id) => id !== optionId);
                      onUpdateField(fieldConfig.fieldId, {
                        valueFilter: {
                          ...fieldConfig.valueFilter,
                          type: 'option_selected',
                          selectedOptions: newOptions,
                        },
                      });
                    }}
                    size='small'
                    style={{
                      padding: 0,
                      marginRight: '6px',
                      color: selectedOptions.includes(optionId) ? '#7047EB' : undefined,
                    }}
                  />
                }
                label={<Typography className={classes.checkboxLabel}>{option.name}</Typography>}
                style={{ marginLeft: 0 }}
              />
            );
          })}
        </Box>
      );
    }

    return <Typography color='textSecondary'>No value filters available for this field type</Typography>;
  };

  return (
    <Box className={classes.filterConfigBox}>
      {/* Task Title */}
      <Typography className={classes.fieldTitle}>{taskConfig.taskTitle}</Typography>

      {/* Task-level completion filter */}
      <FormControlLabel
        control={
          <Checkbox
            checked={showCompletion}
            onChange={(e) => {
              setShowCompletion(e.target.checked);
              if (e.target.checked) {
                onUpdateTask({ completionFilter: { type: 'completed' } });
              } else {
                onUpdateTask({ completionFilter: undefined });
              }
            }}
            size='small'
            style={{ padding: 0, marginRight: '6px', color: showCompletion ? '#7047EB' : undefined }}
          />
        }
        label={<Typography className={classes.checkboxLabel}>Filter by task completion</Typography>}
        style={{ marginLeft: 0, marginTop: '12px' }}
      />

      {showCompletion && (
        <Box ml={4} mt={1} mb={2}>
          <FormControl
            variant='outlined'
            fullWidth
            size='small'
            className={classes.formControl}
            style={{ marginBottom: '8px' }}
          >
            <InputLabel>Completion Filter</InputLabel>
            <Select
              value={taskConfig.completionFilter?.type || 'completed'}
              onChange={(e) =>
                onUpdateTask({
                  completionFilter: { ...taskConfig.completionFilter, type: e.target.value as any },
                })
              }
              label='Completion Filter'
              style={{ fontSize: '13px' }}
              inputProps={{ style: { fontSize: '13px', padding: '6px 14px' } }}
            >
              <MenuItem value='completed' className={classes.menuItem}>
                Completed
              </MenuItem>
              <MenuItem value='not_completed' className={classes.menuItem}>
                Incomplete
              </MenuItem>
              <MenuItem value='completed_on' className={classes.menuItem}>
                Completed on specific date
              </MenuItem>
              <MenuItem value='completed_before' className={classes.menuItem}>
                Completed before date
              </MenuItem>
              <MenuItem value='completed_after' className={classes.menuItem}>
                Completed after date
              </MenuItem>
              <MenuItem value='completed_between' className={classes.menuItem}>
                Completed between dates
              </MenuItem>
            </Select>
          </FormControl>

          {taskConfig.completionFilter?.type === 'completed_between' ? (
            <Box>
              <DatePicker
                variant='inline'
                inputVariant='outlined'
                label='From Date'
                fullWidth
                size='small'
                value={
                  taskConfig.completionFilter?.dateFrom ? DateTime.fromISO(taskConfig.completionFilter.dateFrom) : null
                }
                onChange={(date) =>
                  onUpdateTask({
                    completionFilter: { ...taskConfig.completionFilter, dateFrom: date?.toISODate() || undefined },
                  })
                }
                style={{ marginBottom: '8px' }}
              />
              <DatePicker
                variant='inline'
                inputVariant='outlined'
                label='To Date'
                fullWidth
                size='small'
                value={
                  taskConfig.completionFilter?.dateTo ? DateTime.fromISO(taskConfig.completionFilter.dateTo) : null
                }
                onChange={(date) =>
                  onUpdateTask({
                    completionFilter: { ...taskConfig.completionFilter, dateTo: date?.toISODate() || undefined },
                  })
                }
              />
            </Box>
          ) : (
            (taskConfig.completionFilter?.type === 'completed_on' ||
              taskConfig.completionFilter?.type === 'completed_before' ||
              taskConfig.completionFilter?.type === 'completed_after') && (
              <DatePicker
                variant='inline'
                inputVariant='outlined'
                label='Date'
                fullWidth
                size='small'
                value={taskConfig.completionFilter?.date ? DateTime.fromISO(taskConfig.completionFilter.date) : null}
                onChange={(date) =>
                  onUpdateTask({
                    completionFilter: { ...taskConfig.completionFilter, date: date?.toISODate() || undefined },
                  })
                }
              />
            )
          )}
        </Box>
      )}

      {/* Field-level filters */}
      {taskConfig.fields.map((fieldConfig) => {
        const showValue = fieldStates[fieldConfig.fieldId]?.showValue || false;

        return (
          <Box key={fieldConfig.fieldId} className={classes.fieldBox}>
            <Typography className={classes.fieldSubtitle}>
              {fieldConfig.fieldType.replace('VOLUNTEER_', '').replace('TaskFieldType', '')}
            </Typography>
            <Typography className={classes.fieldTitle} style={{ fontSize: '13px', fontWeight: 600 }}>
              {fieldConfig.fieldName}
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={showValue}
                  onChange={() => toggleFieldValue(fieldConfig.fieldId)}
                  size='small'
                  style={{ padding: 0, marginRight: '6px', color: showValue ? '#7047EB' : undefined }}
                />
              }
              label={<Typography className={classes.checkboxLabel}>Filter by field value</Typography>}
              style={{ marginLeft: 0, marginTop: '8px' }}
            />

            {showValue && (
              <Box ml={4} mt={1}>
                {renderFieldValueFilter(fieldConfig)}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
